import React, { useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import TopNav from './TopNav';
import Footer from './Footer';
import useSEO from '../hooks/useSEO';

const roles = [
  {
    id: 'software-developer',
    title: 'Software Developer – AI & Simulation',
    location: 'Remote',
    startDate: 'Q2 2026',
    summary: 'Build AI-assisted, code-free simulation and modeling tools that help leaders make better decisions. Work at the intersection of simulation engines, optimization, and intelligent user experiences.',
    skills: [
      'Python and modern web stack (React, TypeScript)',
      'Experience with AI/ML integration, LLMs, and agentic systems',
      'Background in simulation, optimization, or computational modeling',
      'Systems thinking and comfort with ambiguity',
      'Strong ownership and shipping mentality'
    ],
    responsibilities: [
      'Design and build AI-assisted modeling workflows and simulation engines',
      'Integrate LLMs and agentic AI for model building, calibration, and insight generation',
      'Develop intuitive interfaces that make complex simulation accessible',
      'Own features end-to-end from design through deployment',
      'Collaborate closely with product and customers on high-impact features'
    ],
    success: 'In your first 6–12 months, you\'ll have shipped meaningful features to production, contributed to our AI orchestration layer, and helped shape how users interact with simulation. You\'ll be known for solving hard problems and moving fast without breaking things.'
  },
  {
    id: 'product-marketing',
    title: 'AI Product Marketing Manager',
    location: 'Remote',
    startDate: 'Q2 2026',
    summary: 'Translate complex AI and simulation capabilities into clear, compelling business value. Build the narrative that helps decision-makers understand why simulation matters—without hype or jargon.',
    skills: [
      'Experience marketing technical products to enterprise or technical audiences',
      'Ability to understand and articulate AI, simulation, or analytics concepts',
      'Strong writing and storytelling instincts',
      'Comfort working closely with product and engineering',
      'Preference for substance over buzzwords'
    ],
    responsibilities: [
      'Develop positioning, messaging, and go-to-market strategy for Veydra\'s platform',
      'Create content that builds credibility: case studies, thought leadership, product narratives',
      'Work with founders and early customers to understand and communicate value',
      'Support sales with materials that resonate with senior operations and strategy leaders',
      'Build Veydra\'s presence in decision science and AI communities'
    ],
    success: 'In your first 6–12 months, you\'ll have established Veydra\'s market positioning, created content that drives qualified conversations, and built a repeatable go-to-market motion. Decision-makers will describe Veydra as the company that "actually makes sense."'
  },
  {
    id: 'solutions-engineer',
    title: 'Solutions Architect / Consultant',
    location: 'Remote / Global',
    startDate: 'Available Now',
    summary: 'Bridge the gap between customer challenges and platform capabilities. Lead solution architecture, customer workshops, and integrations that turn complex decisions into working models.',
    skills: [
      'Experience in solution architecture, pre-sales engineering, or technical consulting',
      'Comfort with APIs, Python, data pipelines, and system integration',
      'Background in simulation, optimization, operations research, or decision science',
      'Strong customer-facing communication and workshop facilitation',
      'Systems thinking and consultative problem-solving'
    ],
    responsibilities: [
      'Lead discovery and solution design for enterprise and public-sector customers',
      'Design and prototype simulations that address high-stakes operational decisions',
      'Build reusable playbooks, templates, and integration patterns',
      'Partner with sales on pre-sales engagements and technical validation',
      'Translate customer requirements into product feedback and roadmap input'
    ],
    success: 'In your first 6–12 months, you\'ll have led multiple customer engagements from discovery to implementation design, created reusable solution patterns, and become the go-to expert for turning complex customer problems into working simulations.'
  },
  {
    id: 'enterprise-sales',
    title: 'Enterprise Sales / Strategic Partnerships',
    location: 'Remote',
    startDate: 'Available Now',
    summary: 'Sell decision-intelligence platforms to senior operations, analytics, and strategy leaders. Navigate long-cycle, consultative sales where credibility and discovery matter more than volume.',
    skills: [
      'Experience selling complex, high-value solutions to enterprise buyers',
      'Comfort with long sales cycles and multi-stakeholder decisions',
      'Ability to engage senior leaders on strategic and operational challenges',
      'Background in analytics, simulation, or decision-support tools a plus',
      'Partnership mindset and founder-assisted selling experience'
    ],
    responsibilities: [
      'Identify and engage senior decision-makers in target industries',
      'Lead discovery conversations that uncover high-stakes operational challenges',
      'Navigate complex buying processes with multiple stakeholders',
      'Partner with solutions engineering and founders on strategic deals',
      'Build and manage pipeline for Q2 2026 and beyond'
    ],
    success: 'In your first 6–12 months, you\'ll have built meaningful pipeline, closed initial enterprise deals, and established trusted relationships with senior leaders. You\'ll be known for asking the right questions and matching the right customers to the right solutions.'
  }
];

function CareersPage() {
  useSEO({
    title: 'Careers at Veydra',
    description: 'Join Veydra. Open roles in AI, simulation engineering, product marketing, and more. Remote-first.'
  });

  const [openRole, setOpenRole] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    linkedin: '',
    rolesOfInterest: [],
    interest: '',
    resume: null
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const toggleRole = (roleId) => {
    setOpenRole(openRole === roleId ? null : roleId);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRoleToggle = (roleId) => {
    setFormData(prev => ({
      ...prev,
      rolesOfInterest: prev.rolesOfInterest.includes(roleId)
        ? prev.rolesOfInterest.filter(r => r !== roleId)
        : [...prev.rolesOfInterest, roleId]
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Store file name for display, actual upload would need additional handling
      setFormData(prev => ({ ...prev, resume: file.name }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      await addDoc(collection(db, 'form-submissions-incremental'), {
        name: formData.name,
        email: formData.email,
        linkedin: formData.linkedin,
        rolesOfInterest: formData.rolesOfInterest,
        interest: formData.interest,
        resumeFileName: formData.resume,
        source: 'careers-interest',
        intent: 'job_applicant',
        page: '/careers',
        submittedAt: serverTimestamp()
      });
      setSubmitted(true);
    } catch (err) {
      console.error('Error submitting form:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <TopNav />

      {/* Hero Section */}
      <section className="py-20 md:py-28 px-8">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6">Join Us</p>
          <h1 className="text-5xl md:text-5xl font-medium text-gray-900 mb-6 leading-tight tracking-tight">
            Careers at Veydra
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-6">
            We're building AI-powered decision intelligence—simulation and digital twins that help leaders make better decisions before acting in the real world.
          </p>
          <p className="text-lg text-gray-500 max-w-2xl mx-auto leading-relaxed">
            From enterprise operations to public policy, we help organizations model complexity, quantify uncertainty, and find better paths forward.
          </p>
        </div>
      </section>

      {/* About Veydra */}
      <section className="py-16 md:py-20 px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-medium text-gray-900 mb-8 tracking-tight">About Veydra</h2>
          
          <div className="space-y-6 text-gray-600 leading-relaxed">
            <p>
              Veydra builds AI-assisted simulation and decision-intelligence platforms. We combine system dynamics, discrete-event simulation, optimization, and agentic AI to help organizations understand complex systems and make better decisions.
            </p>
            <p>
              Our customers include operations leaders, analysts, educators, and strategists working on high-stakes decisions—from supply chain optimization and workforce planning to policy impact analysis and capital allocation.
            </p>
            <p>
              We're a remote-first, small senior team that values shipping real value over theoretical elegance. We're product-driven and research-informed, not the other way around.
            </p>
          </div>

          {/* Culture Values */}
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="font-medium text-gray-900 mb-2">Clarity over complexity</h3>
              <p className="text-sm text-gray-600">We make complex ideas accessible. If we can't explain it simply, we don't understand it well enough.</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="font-medium text-gray-900 mb-2">Intellectual honesty</h3>
              <p className="text-sm text-gray-600">We say what we think, acknowledge what we don't know, and change our minds when evidence warrants.</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="font-medium text-gray-900 mb-2">Systems thinking</h3>
              <p className="text-sm text-gray-600">We see connections, feedback loops, and second-order effects. We model before we act.</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="font-medium text-gray-900 mb-2">Ownership and outcomes</h3>
              <p className="text-sm text-gray-600">We own problems end-to-end and measure success by impact, not activity.</p>
            </div>
          </div>

          <p className="mt-8 text-gray-600 leading-relaxed">
            We're committed to building an inclusive team where different perspectives strengthen our work. We value transparency, outcome-focused collaboration, and people who bring both rigor and humility.
          </p>
        </div>
      </section>

      {/* Open Roles */}
      <section className="py-16 md:py-20 px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-medium text-gray-900 mb-4 tracking-tight">Open Roles</h2>
          <p className="text-gray-600 mb-8">Target start: Q2 2026. Click a role to learn more.</p>

          <div className="space-y-4">
            {roles.map((role) => (
              <div key={role.id} className="border border-gray-200 rounded-lg overflow-hidden">
                {/* Accordion Header */}
                <button
                  onClick={() => toggleRole(role.id)}
                  className="w-full px-6 py-5 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors text-left"
                >
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">{role.title}</h3>
                    <p className="text-sm text-gray-500 mt-1">{role.location} · {role.startDate}</p>
                  </div>
                  <svg
                    className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${openRole === role.id ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Accordion Content */}
                <div
                  className={`transition-all duration-300 ease-in-out overflow-hidden ${
                    openRole === role.id ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-6 pb-6 pt-2 border-t border-gray-100">
                    <p className="text-gray-600 mb-6 leading-relaxed">{role.summary}</p>

                    <div className="mb-6">
                      <h4 className="text-sm font-medium text-gray-900 uppercase tracking-wider mb-3">Core Skills</h4>
                      <ul className="space-y-2">
                        {role.skills.map((skill, i) => (
                          <li key={i} className="flex items-start text-gray-600 text-sm">
                            <span className="text-gray-400 mr-3">—</span>
                            {skill}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="mb-6">
                      <h4 className="text-sm font-medium text-gray-900 uppercase tracking-wider mb-3">Responsibilities</h4>
                      <ul className="space-y-2">
                        {role.responsibilities.map((resp, i) => (
                          <li key={i} className="flex items-start text-gray-600 text-sm">
                            <span className="text-gray-400 mr-3">—</span>
                            {resp}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-gray-900 mb-2">What success looks like</h4>
                      <p className="text-sm text-gray-600 leading-relaxed">{role.success}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Interest Form */}
      <section className="py-16 md:py-20 px-8 bg-gray-50">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-medium text-gray-900 mb-4 tracking-tight text-center">Express Interest</h2>
          <p className="text-gray-600 text-center mb-8">
            We're building our hiring pipeline for Q2 2026. Submitting interest does not require immediate availability.
          </p>

          {!submitted ? (
            <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-lg p-8">
              <div className="space-y-6">
                {/* Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-900 mb-2">
                    Name <span className="text-gray-400">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-shadow"
                    placeholder="Your full name"
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-900 mb-2">
                    Email <span className="text-gray-400">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-shadow"
                    placeholder="you@example.com"
                  />
                </div>

                {/* LinkedIn */}
                <div>
                  <label htmlFor="linkedin" className="block text-sm font-medium text-gray-900 mb-2">
                    LinkedIn Profile
                  </label>
                  <input
                    type="url"
                    id="linkedin"
                    name="linkedin"
                    value={formData.linkedin}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-shadow"
                    placeholder="https://linkedin.com/in/yourprofile"
                  />
                </div>

                {/* Roles of Interest */}
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-3">
                    Role(s) of interest
                  </label>
                  <div className="space-y-3">
                    {roles.map((role) => (
                      <label key={role.id} className="flex items-start cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={formData.rolesOfInterest.includes(role.id)}
                          onChange={() => handleRoleToggle(role.id)}
                          className="mt-1 h-4 w-4 text-gray-900 border-gray-300 rounded focus:ring-gray-900"
                        />
                        <span className="ml-3 text-sm text-gray-600 group-hover:text-gray-900">{role.title}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Interest */}
                <div>
                  <label htmlFor="interest" className="block text-sm font-medium text-gray-900 mb-2">
                    What interests you about Veydra?
                  </label>
                  <textarea
                    id="interest"
                    name="interest"
                    rows={4}
                    value={formData.interest}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-shadow resize-none"
                    placeholder="Tell us a bit about yourself and why you're interested..."
                  />
                </div>

                {/* Resume Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Resume <span className="text-gray-400">(optional)</span>
                  </label>
                  <div className="flex items-center gap-4">
                    <label className="cursor-pointer">
                      <span className="inline-block px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:border-gray-400 transition-colors">
                        Choose file
                      </span>
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={handleFileChange}
                        className="hidden"
                      />
                    </label>
                    {formData.resume && (
                      <span className="text-sm text-gray-500">{formData.resume}</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-2">PDF, DOC, or DOCX</p>
                </div>

                {error && (
                  <p className="text-sm text-red-600">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-gray-900 text-white px-6 py-3 rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Submitting...' : 'Submit Interest'}
                </button>
              </div>
            </form>
          ) : (
            <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">Thanks for your interest</h3>
              <p className="text-gray-600">
                We've received your submission and will be in touch as we build our team for Q2 2026.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-16 md:py-20 px-8">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-medium text-gray-900 mb-6 tracking-tight">
            Build real systems, not slideware
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed mb-4">
            We think in models, not slogans. If you enjoy ambiguity, value rigor, and want your work to have real impact on how organizations make decisions—we'd love to hear from you.
          </p>
          <p className="text-gray-500">
            Questions? Reach out at <a href="mailto:careers@veydra.com" className="text-gray-900 hover:underline">careers@veydra.com</a>
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default CareersPage;
